/* The mockup's `.preview-card` + `.pv-sample`: a card with a sample screen drawn from the `--pv-*` properties. */
.card {
  background: var(--surface-raised);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
}

.head {
  display: flex;
  align-items: center;
  min-block-size: 48px;
  padding: var(--space-3) var(--space-4);
  border-block-end: 1px solid var(--border);
}

.title {
  margin: 0;
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-semibold);
}

/* Read by assistive technology only: the sample itself is a picture. */
.description {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Neutral fallbacks until a colour is valid: the app's own aliases, never a literal colour. */
.sample {
  --pv-fallback-surface: var(--surface-raised);
  --pv-fallback-text: var(--text);
  --pv-fallback-primary: var(--text-muted);
  /* The mockup's faint hairlines (a quarter and two fifths of grey): the sample's own text colour, thinned. */
  --pv-line: color-mix(in srgb, currentColor 25%, transparent);
  --pv-line-strong: color-mix(in srgb, currentColor 40%, transparent);
  padding: calc(var(--pv-space, 8px) * 3);
  background: var(--pv-surface, var(--pv-fallback-surface));
  color: var(--pv-text, var(--pv-fallback-text));
  font-family: var(--pv-body-font, inherit);
  font-size: var(--pv-body-size, 16px);
  line-height: 1.5;
}

.bar {
  display: flex;
  align-items: center;
  gap: var(--pv-space, 8px);
  margin-block-end: calc(var(--pv-space, 8px) * 2.5);
  font-weight: 600;
}

.logo,
.logoImage {
  inline-size: 24px;
  block-size: 24px;
  border-radius: calc(var(--pv-radius, 8px) * 0.75);
}

.logo {
  background: var(--pv-primary, var(--pv-fallback-primary));
}

.logoImage {
  object-fit: contain;
}

.heading {
  margin: 0 0 6px;
  font-family: var(--pv-heading-font, inherit);
  font-size: var(--pv-heading-size, 25px);
  line-height: 1.2;
}

.body {
  margin: 0 0 calc(var(--pv-space, 8px) * 2);
  opacity: 0.8;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--pv-space, 8px);
}

.primary,
.secondary {
  padding: var(--pv-space, 8px) calc(var(--pv-space, 8px) * 1.75);
  border-radius: var(--pv-radius, 8px);
  font-weight: 600;
}

.primary {
  background: var(--pv-primary, var(--pv-fallback-primary));
  color: var(--pv-surface, var(--pv-fallback-surface));
}

/* Mockup v3 P5: the secondary colour is the soft fill of supporting buttons; the text stays the text. */
.secondary {
  border: 1px solid currentColor;
  background: var(--pv-secondary, transparent);
  font-weight: 400;
}

.input {
  display: block;
  margin: calc(var(--pv-space, 8px) * 2) 0;
  padding: var(--pv-space, 8px) calc(var(--pv-space, 8px) * 1.25);
  border: 1px solid var(--pv-line-strong);
  border-radius: var(--pv-radius, 8px);
  opacity: 0.6;
}

.tile {
  display: flex;
  justify-content: space-between;
  padding: calc(var(--pv-space, 8px) * 1.75);
  border: 1px solid var(--pv-line);
  border-radius: var(--pv-radius, 8px);
}

.tag {
  padding: 0 8px;
  border-radius: 999px;
  background: var(--pv-primary, var(--pv-fallback-primary));
  color: var(--pv-surface, var(--pv-fallback-surface));
  font-size: 12px;
  font-weight: 600;
}

.states {
  display: flex;
  flex-wrap: wrap;
  gap: var(--pv-space, 8px);
  margin-block-start: calc(var(--pv-space, 8px) * 2);
  font-size: 12px;
  font-weight: 600;
}

.success {
  color: var(--pv-success, var(--pv-fallback-text));
}

.warning {
  color: var(--pv-warning, var(--pv-fallback-text));
}

.danger {
  color: var(--pv-danger, var(--pv-fallback-text));
}
